import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import './style.less';
import { VisitorActivity } from 'sb-lib/types';
import { getVisitorActions } from 'shared/api';
import { Button } from 'antd';
import { useAppSelector } from 'shared/store';
import clsx from 'clsx';
import { SpotlightAccountTabsActivityGroup } from 'browser/components/Spotlight/Account/Tabs/Activity/ActivityGroup';
import { SpotlightActivityItemSkeleton } from 'browser/components/Spotlight/Account/Tabs/Activity/ActivityItemSkeleton';
import { useOutbound } from 'browser/components/Outbound/useOutbound';

export const VisitorOverviewActivity: React.FC<{ visitorId?: string }> = ({
  visitorId,
}) => {
  const [loading, setLoading] = useState(true);
  const [nextPage, setNextPage] = useState<number | undefined>(1);

  const {
    apolloCallDispositions,
    apolloContactLists,
    hubspotCallDispositions,
    hubspotContactLists,
    outreachCallDispositions,
    outreachContactLists,
    salesforceCallDispositions,
    salesforceContactLists,
    salesloftCallDispositions,
    salesloftContactLists,
    servicebellCallDispositions,
    servicebellContactLists,
  } = useAppSelector(s => s.outbound);
  const { loadOutbound } = useOutbound();

  useEffect(() => {
    loadOutbound();
  }, [loadOutbound]);

  const [items, setItems] = useState<VisitorActivity[]>([]);

  const loadData = useCallback(() => {
    if (!visitorId) {
      return;
    }

    setLoading(true);

    getVisitorActions(visitorId, nextPage).then(({ data }) => {
      setItems(v => (nextPage !== 1 ? [...v, ...data.items] : data.items));
      setNextPage(!data.nextPage ? undefined : data.nextPage);
      setLoading(false);
    });
  }, [nextPage, visitorId]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const organisedAndSortedData = useMemo(() => {
    const combinedData = items;
    const sortedData = combinedData.sort((a, b) => {
      const aDate = a.startTime;
      const bDate = b.startTime;
      /* @ts-ignore */
      return new Date(bDate) - new Date(aDate);
    });

    return sortedData.reduce<{
      [date: string]: VisitorActivity[];
    }>((acc, curr) => {
      const dateKey = curr.startTime;
      // Assuming the date is in UTC, convert it to the user's local timezone
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const zonedDate = zonedTimeToUtc(dateKey, userTimeZone);
      const formattedDate = formatInTimeZone(zonedDate, userTimeZone, 'yyyy-MM-dd');

      if (!acc[formattedDate]) {
        acc[formattedDate] = [];
      }
      acc[formattedDate].push(curr);
      return acc;
    }, {});
  }, [items]);

  return (
    <div className="VisitorOverviewActivity">
      {Object.entries(organisedAndSortedData).map(([key, items]) => {
        return (
          <SpotlightAccountTabsActivityGroup
            key={key}
            label={key}
            items={items}
            {...{
              apolloCallDispositions,
              apolloContactLists,
              hubspotCallDispositions,
              hubspotContactLists,
              outreachCallDispositions,
              outreachContactLists,
              salesforceCallDispositions,
              salesforceContactLists,
              salesloftCallDispositions,
              salesloftContactLists,
              servicebellCallDispositions,
              servicebellContactLists,
            }}
          />
        );
      })}

      {!items.length && !nextPage && (
        <div className="VisitorOverviewActivity__empty">
          <div className="VisitorOverviewActivity__emptyContent">
            <div className="VisitorOverviewActivity__emptyTitle">No activity found</div>
            <div className="VisitorOverviewActivity__emptyDesc">
              We couldn't find any activity for this account.
            </div>
          </div>
        </div>
      )}

      {loading &&
        [...new Array(3)].map((_, i) => {
          return <SpotlightActivityItemSkeleton key={i} />;
        })}

      <Button
        className={clsx(
          'VisitorOverviewActivity__loadMore',
          !nextPage && !loading && 'VisitorOverviewActivity__loadMore--hidden',
        )}
        type="ghost"
        disabled={loading}
        onClick={() => {
          if (!nextPage) return;
          loadData();
        }}
      >
        Load More
      </Button>
    </div>
  );
};
